
// client/src/components/CommentBox.jsx
import React, { useState, useRef, useEffect } from 'react';
import { searchUsers } from '../../services/messagesApi';
import { getSessionLocal } from '../../services/sessionLocal';
import './CommentBox.css';

/**
 * CommentBox with @mention support
 * When user types @, shows autocomplete dropdown with users from tenant
 */
export default function CommentBox({ 
  value = '', 
  onChange, 
  placeholder = 'Add a comment...', 
  onSubmit,
  rows = 4 
}) {
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionResults, setMentionResults] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionStartPos, setMentionStartPos] = useState(-1);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const textareaRef = useRef(null);
  const mentionListRef = useRef(null);

  // Get tenant from session
  const session = getSessionLocal();
  const tenantId = session?.tenantId || session?.tenantid;

  // Detect @ mentions
  useEffect(() => {
    if (!value) {
      setShowMentions(false);
      return;
    }

    const cursorPos = textareaRef.current?.selectionStart || value.length;
    const textBeforeCursor = value.substring(0, cursorPos);
    
    // Find last @ before cursor
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex === -1) {
      setShowMentions(false);
      return;
    }

    // Check if there's a space after the @ (which ends the mention)
    const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
    if (textAfterAt.includes(' ') || textAfterAt.includes('\n')) {
      setShowMentions(false);
      return;
    }

    // Extract query after @
    const query = textAfterAt.trim();
    setMentionQuery(query);
    setMentionStartPos(lastAtIndex);
    setShowMentions(true);
    setSelectedIndex(0);

    // Search for users if query is not empty
    if (query.length > 0) {
      searchUsersDebounced(query);
    } else {
      setMentionResults([]);
    }
  }, [value, tenantId]);

  // Debounced user search
  const searchUsersDebounced = useRef(
    debounce(async (query) => {
      if (!tenantId || query.length < 1) {
        setMentionResults([]);
        return;
      }

      try {
        const result = await searchUsers(query, { tenantId });
        const users = result?.users || [];
        setMentionResults(users.slice(0, 10)); // Limit to 10 results
      } catch (error) {
        console.error('User search failed:', error);
        setMentionResults([]);
      }
    }, 300)
  ).current;

  // Handle mention selection
  const selectMention = (user) => {
    if (!textareaRef.current || mentionStartPos === -1) return;

    const username = user?.username || user?.displayName || user?.email || 'Unknown';
    const beforeMention = value.substring(0, mentionStartPos);
    const afterMention = value.substring(textareaRef.current.selectionStart);
    
    const newValue = beforeMention + `@${username} ` + afterMention;
    onChange({ target: { value: newValue } });

    // Reset mention state
    setShowMentions(false);
    setMentionQuery('');
    setMentionResults([]);
    setMentionStartPos(-1);

    // Focus back on textarea
    setTimeout(() => {
      textareaRef.current?.focus();
      const newCursorPos = beforeMention.length + username.length + 2;
      textareaRef.current?.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showMentions || mentionResults.length === 0) {
      // Allow form submission on Enter if not showing mentions
      if (e.key === 'Enter' && !e.shiftKey && onSubmit) {
        e.preventDefault();
        onSubmit();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < mentionResults.length - 1 ? prev + 1 : 0
        );
        break;
      
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : mentionResults.length - 1
        );
        break;
      
      case 'Enter':
        if (showMentions && mentionResults[selectedIndex]) {
          e.preventDefault();
          selectMention(mentionResults[selectedIndex]);
        }
        break;
      
      case 'Escape':
        e.preventDefault();
        setShowMentions(false);
        setMentionResults([]);
        break;
      
      default:
        break;
    }
  };

  // Scroll selected item into view
  useEffect(() => {
    if (mentionListRef.current && showMentions) {
      const selectedItem = mentionListRef.current.children[selectedIndex];
      if (selectedItem) {
        selectedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex, showMentions]);

  return (
    <div className="comment-box-wrapper">
      <textarea
        ref={textareaRef}
        className="comment-textarea form-control"
        value={value}
        onChange={onChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
      />
      
      {showMentions && mentionResults.length > 0 && (
        <div className="mention-suggestions" ref={mentionListRef}>
          {mentionResults.map((user, index) => {
            const username = user?.username || user?.displayName || 'Unknown';
            const email = user?.email || '';
            
            return (
              <div
                key={user?.id || index}
                className={`suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
                onClick={() => selectMention(user)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <div className="suggestion-username">{username}</div>
                {email && <div className="suggestion-email">{email}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Simple debounce function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}